import {
  ExportResultCode,
  hrTimeToMicroseconds,
  type ExportResult,
} from '@opentelemetry/core';
import type { ReadableSpan, SpanExporter } from '@opentelemetry/sdk-trace-base';

interface CustomOTLPExporterConfig {
  url?: string;
  headers?: Record<string, string>;
  timeoutMillis?: number;
  concurrencyLimit?: number;
}

export class CustomOTLPTraceExporter implements SpanExporter {
  private _url: string;
  private _headers: Record<string, string>;
  private _timeoutMillis: number;
  private _concurrentRequests = 0;
  private _maxConcurrentRequests: number;
  private _isPageUnloading = false;

  constructor(config: CustomOTLPExporterConfig = {}) {
    this._url = config.url || '';
    this._headers = {
      'Content-Type': 'application/json',
      ...config.headers,
    };
    this._timeoutMillis = config.timeoutMillis || 30000;
    this._maxConcurrentRequests = config.concurrencyLimit || 30;

    if (typeof window !== 'undefined') {
      // Listen for pagehide event to detect when the page is being unloaded.
      // This event triggers when:
      // 1. Closing tab/window
      // 2. Refreshing page (F5, Cmd+R)
      // 3. Navigating to another page (clicking links, address bar input)
      // 4. Browser back/forward navigation
      // 5. Page enters BFCache (Safari/Firefox)
      //
      // Note: Does NOT trigger on tab switching or window minimize.
      // When page is unloading, use sendBeacon/fetch keepalive to ensure data delivery.
      window.addEventListener('pagehide', () => {
        this._isPageUnloading = true;
      });
    }
  }

  export(
    spans: ReadableSpan[],
    resultCallback: (result: ExportResult) => void,
  ): void {
    if (spans.length === 0) {
      resultCallback({ code: ExportResultCode.SUCCESS });
      return;
    }

    if (this._concurrentRequests >= this._maxConcurrentRequests) {
      console.warn(
        `[CustomOTLPExporter] Concurrent request limit reached (${this._maxConcurrentRequests}), dropping export`,
      );
      resultCallback({
        code: ExportResultCode.FAILED,
        error: new Error('Concurrent request limit reached'),
      });
      return;
    }

    try {
      const payload = this._serializeSpans(spans);

      if (this._isPageUnloading) {
        this._sendWithBeacon(payload, resultCallback);
      } else {
        this._sendWithXHR(payload, resultCallback);
      }
    } catch (error) {
      resultCallback({
        code: ExportResultCode.FAILED,
        error:
          error instanceof Error ? error : new Error('Serialization failed'),
      });
    }
  }

  private _serializeSpans(spans: ReadableSpan[]): string {
    const resourceSpans = new Map<string, any>();

    spans.forEach((span) => {
      const resourceKey = JSON.stringify(span.resource.attributes);

      if (!resourceSpans.has(resourceKey)) {
        resourceSpans.set(resourceKey, {
          resource: {
            attributes: this._serializeAttributes(span.resource.attributes),
          },
          scopeSpans: [],
        });
      }

      const rs = resourceSpans.get(resourceKey);
      const scopeKey = `${span.instrumentationScope.name}@${span.instrumentationScope.version || ''}`;

      let scopeSpan = rs.scopeSpans.find(
        (s: any) => `${s.scope.name}@${s.scope.version || ''}` === scopeKey,
      );

      if (!scopeSpan) {
        scopeSpan = {
          scope: {
            name: span.instrumentationScope.name,
            version: span.instrumentationScope.version || '',
          },
          spans: [],
        };
        rs.scopeSpans.push(scopeSpan);
      }

      const parentSpanId = (span as any).parentSpanId;

      scopeSpan.spans.push({
        traceId: span.spanContext().traceId,
        spanId: span.spanContext().spanId,
        ...(parentSpanId && { parentSpanId }),
        name: span.name,
        kind: span.kind,
        startTimeUnixNano: hrTimeToMicroseconds(span.startTime) * 1000,
        endTimeUnixNano: hrTimeToMicroseconds(span.endTime) * 1000,
        attributes: this._serializeAttributes(span.attributes),
        status: span.status,
        events: span.events.map((event) => ({
          timeUnixNano: hrTimeToMicroseconds(event.time) * 1000,
          name: event.name,
          attributes: this._serializeAttributes(event.attributes || {}),
        })),
        links: span.links.map((link) => ({
          traceId: link.context.traceId,
          spanId: link.context.spanId,
          attributes: this._serializeAttributes(link.attributes || {}),
        })),
      });
    });

    return JSON.stringify({
      resourceSpans: Array.from(resourceSpans.values()),
    });
  }

  private _serializeAttributes(attributes: Record<string, any>): any[] {
    return Object.entries(attributes).map(([key, value]) => {
      let valueType: string;
      let serializedValue: any;

      if (typeof value === 'string') {
        valueType = 'stringValue';
        serializedValue = value;
      } else if (typeof value === 'number') {
        if (Number.isInteger(value)) {
          valueType = 'intValue';
          serializedValue = value.toString();
        } else {
          valueType = 'doubleValue';
          serializedValue = value;
        }
      } else if (typeof value === 'boolean') {
        valueType = 'boolValue';
        serializedValue = value;
      } else if (Array.isArray(value)) {
        valueType = 'arrayValue';
        serializedValue = {
          values: value.map((v) => ({ stringValue: String(v) })),
        };
      } else {
        valueType = 'stringValue';
        serializedValue = JSON.stringify(value);
      }

      return {
        key,
        value: { [valueType]: serializedValue },
      };
    });
  }

  private _sendWithBeacon(
    payload: string,
    resultCallback: (result: ExportResult) => void,
  ): void {
    const MAX_KEEPALIVE_SIZE = 200 * 1024;
    const payloadSize = new Blob([payload]).size;

    if (payloadSize > MAX_KEEPALIVE_SIZE) {
      console.warn(
        `[CustomOTLPExporter] Payload too large for keepalive (${payloadSize} bytes), dropping spans. Consider reducing maxExportBatchSize.`,
      );
      resultCallback({
        code: ExportResultCode.FAILED,
        error: new Error(
          `Payload exceeds keepalive limit (${payloadSize} > ${MAX_KEEPALIVE_SIZE})`,
        ),
      });
      return;
    }

    const hasCustomHeaders = Object.keys(this._headers).some(
      (key) => key.toLowerCase() !== 'content-type',
    );

    if (
      !hasCustomHeaders &&
      typeof navigator !== 'undefined' &&
      navigator.sendBeacon
    ) {
      const blob = new Blob([payload], { type: 'application/json' });
      const success = navigator.sendBeacon(this._url, blob);

      if (success) {
        resultCallback({ code: ExportResultCode.SUCCESS });
      } else {
        console.warn(
          '[CustomOTLPExporter] sendBeacon queuing failed, trying fetch keepalive',
        );
        this._sendWithFetchKeepalive(payload, resultCallback);
      }
    } else {
      this._sendWithFetchKeepalive(payload, resultCallback);
    }
  }

  private _sendWithFetchKeepalive(
    payload: string,
    resultCallback: (result: ExportResult) => void,
  ): void {
    try {
      fetch(this._url, {
        method: 'POST',
        headers: this._headers,
        body: payload,
        keepalive: true,
      })
        .then((response) => {
          if (response.ok) {
            resultCallback({ code: ExportResultCode.SUCCESS });
          } else {
            resultCallback({
              code: ExportResultCode.FAILED,
              error: new Error(`HTTP ${response.status}`),
            });
          }
        })
        .catch((error) => {
          resultCallback({
            code: ExportResultCode.FAILED,
            error:
              error instanceof Error
                ? error
                : new Error('Fetch keepalive failed'),
          });
        });
    } catch (error) {
      console.warn(
        '[CustomOTLPExporter] Fetch keepalive threw synchronously, likely quota exceeded',
      );
      resultCallback({
        code: ExportResultCode.FAILED,
        error:
          error instanceof Error
            ? error
            : new Error('Fetch keepalive quota exceeded'),
      });
    }
  }

  private _sendWithXHR(
    payload: string,
    resultCallback: (result: ExportResult) => void,
  ): void {
    this._concurrentRequests++;

    const xhr = new XMLHttpRequest();
    xhr.timeout = this._timeoutMillis;
    xhr.open('POST', this._url);

    Object.entries(this._headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    xhr.ontimeout = () => {
      this._concurrentRequests--;
      resultCallback({
        code: ExportResultCode.FAILED,
        error: new Error('XHR request timed out'),
      });
    };

    xhr.onload = () => {
      this._concurrentRequests--;
      if (xhr.status >= 200 && xhr.status <= 299) {
        resultCallback({ code: ExportResultCode.SUCCESS });
      } else {
        resultCallback({
          code: ExportResultCode.FAILED,
          error: new Error(`HTTP ${xhr.status}: ${xhr.statusText}`),
        });
      }
    };

    xhr.onerror = () => {
      this._concurrentRequests--;
      resultCallback({
        code: ExportResultCode.FAILED,
        error: new Error('XHR request failed'),
      });
    };

    xhr.onabort = () => {
      this._concurrentRequests--;
      resultCallback({
        code: ExportResultCode.FAILED,
        error: new Error('XHR request aborted'),
      });
    };

    xhr.send(payload);
  }

  async forceFlush(): Promise<void> {
    const maxWaitTime = this._timeoutMillis + 5000;
    const startTime = Date.now();

    while (this._concurrentRequests > 0) {
      if (Date.now() - startTime > maxWaitTime) {
        console.warn('[CustomOTLPExporter] forceFlush timeout');
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  async shutdown(): Promise<void> {
    await this.forceFlush();
  }
}
